import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { GetFieldType } from '@app/types';
import { RecordType } from '@app/user.model';
import { TranslateService } from '@ngx-translate/core';
import {
  ButtonColors,
  ConfirmDialogComponent,
  ConfirmDialogModel,
  DialogType,
  DialogVariant,
} from '@shared/confirm-dialog/confirm-dialog.component';
import { saveAs } from 'file-saver';

import { AuthenticationService } from './auth.service';
import * as moment from 'moment';

type DialogOptions = {
  dialog: MatDialog;
  message: string;
  title?: string;
  dialogVariant?: DialogVariant;
  confirmColor?: ButtonColors;
  cancelColor?: ButtonColors;
  closeDialog?: boolean;
  noText?: string;
  yesText?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
};

@Injectable({
  providedIn: 'root',
})
export class CommonService {
  private token: string;
  constructor(
    private httpClient: HttpClient,
    private authService: AuthenticationService,
    private translateService: TranslateService
  ) {
    const currentUser = this.authService.getCurrentUser();
    this.token = currentUser?.token || '';
  }

  downloadFile(url: string, fileName: string, type: string) {
    this.httpClient.get(url, { responseType: 'blob' }).subscribe((response) => {
      if (response) {
        const blobUrl = window.URL.createObjectURL(
          new Blob([response], { type })
        );
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        saveAs(blobUrl, fileName);
      }
    });
  }

  downloadFromPresignedUrl(url: string, fileName?: string): void {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    if (fileName) {
      anchor.download = fileName;
    }
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }

  downloadAttachment(
    file: File | string,
    fileName: string,
    mimeType?: string,
    downloadUrl?: string
  ) {
    if (downloadUrl) {
      this.downloadFromPresignedUrl(downloadUrl, fileName);
      return;
    }

    if (file instanceof File) {
      const blobUrl = window.URL.createObjectURL(file);
      saveAs(blobUrl, fileName);
    } else {
      this.httpClient
        .get(file, { responseType: 'blob' })
        .subscribe((response) => {
          if (response) {
            const blobUrl = window.URL.createObjectURL(
              new Blob([response], { type: mimeType })
            );
            saveAs(blobUrl, fileName);
          }
        });
    }
  }

  async downloadChatMedia(file: any, fileName?: string) {
    // const url = typeof file === 'string' ? file : URL.createObjectURL(file);

    const response = await fetch(file);
    const blob = await response.blob();

    const blobUrl = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = blobUrl;

    console.log('anchor tag =>', a);

    a.download = fileName || 'file';

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    if (file instanceof File) {
      URL.revokeObjectURL(blobUrl);
    }
  }

  downloadReceipt(url: string, type: string) {
    this.httpClient
      .get(url, { responseType: 'blob', observe: 'response' })
      .subscribe((response) => {
        if (response.body) {
          const contentDisposition = response.headers.get(
            'content-disposition'
          );
          const fileNameMatch = contentDisposition
            ? contentDisposition.split('filename=')[1]
            : null;
          const currentDate = moment(new Date()).format('DD-MM-YYYY');
          const fileName = fileNameMatch
            ? fileNameMatch
            : `Healic App Package Receipt_${currentDate}.pdf`;

          const blobUrl = window.URL.createObjectURL(
            new Blob([response.body], { type })
          );
          saveAs(blobUrl, fileName);
        }
      });
  }

  downloadBill(url: string, type: string) {
    this.httpClient
      .get(url, { responseType: 'blob', observe: 'response' })
      .subscribe((response) => {
        if (response.body) {
          const contentDisposition = response.headers.get(
            'content-disposition'
          );
          const fileNameMatch = contentDisposition
            ? contentDisposition.split('filename=')[1]
            : null;
          const currentDate = moment(new Date()).format('DD-MM-YYYY');
          const fileName = fileNameMatch
            ? fileNameMatch
            : `Healic App Bill Receipt_${currentDate}.pdf`;

          const blobUrl = window.URL.createObjectURL(
            new Blob([response.body], { type })
          );
          saveAs(blobUrl, fileName);
        }
      });
  }

  getRecordTypeMap() {
    const recordTypeMap: Map<RecordType, string> = new Map<
      RecordType,
      string
    >();

    recordTypeMap.set(RecordType.DischargeSummary, 'Discharge Summary');
    recordTypeMap.set(RecordType.Imaging, 'Imaging');
    recordTypeMap.set(RecordType.LabReport, 'Lab Report');
    recordTypeMap.set(RecordType.Other, 'Other');
    recordTypeMap.set(RecordType.Prescription, 'Prescription');
    recordTypeMap.set(RecordType.Vital, 'Vital');
    recordTypeMap.set(RecordType.EPrescription, 'EPrescription');
    return recordTypeMap;
  }

  getExtensionFromS3Url(url: string): string {
    const resourceUrl = url.split('?')[0];
    const lastDotIndex = resourceUrl.lastIndexOf('.');
    return resourceUrl.slice(lastDotIndex);
  }

  public getRequestHeaders() {
    return {
      accept: 'application/json',
      authorization: `bearer ${this.token || ''}`,
    };
  }

  getNestedObjectValue<
    TData,
    TPath extends string,
    TDefault = GetFieldType<TData, TPath>
  >(
    data: TData,
    path: TPath,
    defaultValue?: TDefault
  ): GetFieldType<TData, TPath> | TDefault {
    const value = path
      .split(/[.[\]]/)
      .filter(Boolean)
      .reduce<GetFieldType<TData, TPath>>(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access
        (value, key) => (value as any)?.[key],
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        data as any
      );
    return value !== undefined ? value : (defaultValue as TDefault);
  }

  downloadCsv(
    url: string,
    defaultFileName: string,
    type: string = 'application/octet-stream',
    onComplete?: () => void
  ) {
    this.httpClient
      .get(url, { responseType: 'blob', observe: 'response' })
      .subscribe({
        next: (response) => {
          if (response.body) {
            const contentDisposition = response.headers.get(
              'content-disposition'
            );
            let fileName = `${defaultFileName}_${moment().format(
              'DD-MM-YYYY'
            )}.csv`;

            if (contentDisposition) {
              const matches = contentDisposition.match(/filename="?([^"]+)"?/);
              if (matches?.[1]) {
                fileName = matches[1];
              }
            }

            const blob = new Blob([response.body], { type });
            saveAs(blob, fileName);
          }
          if (onComplete) onComplete(); // Ensure the callback is executed
        },
        error: (err) => {
          console.error('Error downloading file:', err);
          if (onComplete) onComplete(); // Ensure the callback is executed even in case of an error
        },
      });
  }

  importCsv(
    url: string,
    defaultFileName: string,
    type: string = 'application/octet-stream',
    onComplete?: () => void
  ) {
    this.httpClient
      .get(url, { responseType: 'blob', observe: 'response' })
      .subscribe({
        next: (response) => {
          if (response.body) {
            const contentDisposition = response.headers.get(
              'content-disposition'
            );
            let fileName = `${defaultFileName}.csv`;

            if (contentDisposition) {
              const matches = contentDisposition.match(/filename="?([^"]+)"?/);
              if (matches?.[1]) {
                fileName = matches[1];
              }
            }

            const blob = new Blob([response.body], { type });
            saveAs(blob, fileName);
          }
          if (onComplete) onComplete(); // Ensure the callback is executed
        },
        error: (err) => {
          console.error('Error downloading file:', err);
          if (onComplete) onComplete(); // Ensure the callback is executed even in case of an error
        },
      });
  }

  /** Backend error code when en.json has a message for it, else the raw API message. */
  getApiErrorMessage(err: any): string {
    const code = err?.error?.code;
    if (code && this.translateService.instant(code) !== code) {
      return code;
    }
    const message = err?.error?.message;
    // DTO validation errors come back as an array of messages
    return (
      (Array.isArray(message) ? message.join(', ') : message) || code || ''
    );
  }

  FailedAlertDialog(
    option: DialogOptions,
    returnRef = false
  ): MatDialogRef<any> | void {
    option.title =
      (this.translateService.instant(
        option.title || 'SomethingWentWrong'
      ) as string) || 'SomethingWentWrong';

    option.message =
      (this.translateService.instant(
        option.message || 'There is error in completing required operation'
      ) as string) || 'There is error in completing required operation';

    const dialogData = new ConfirmDialogModel(
      option.title,
      option.message,
      DialogVariant.OK,
      DialogType.Error
    );

    const ref = option.dialog.open(ConfirmDialogComponent, {
      data: dialogData,
    });
    if (returnRef) {
      return ref;
    }
  }

  confirmDialog(option: DialogOptions): void {
    option.title =
      (this.translateService.instant(option.title || 'Success') as string) ||
      'Success';
    option.message =
      (this.translateService.instant(
        option.message || 'Operation has successfully completed'
      ) as string) || 'Operation has successfully completed';
    option.dialogVariant = DialogVariant.YesNo;

    const dialogData = new ConfirmDialogModel(
      option.title,
      option.message,
      option.dialogVariant,
      DialogType.Success
    );

    const dialogRef = option.dialog.open(ConfirmDialogComponent, {
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((isSuccess) => {
      if (!isSuccess) {
        if (option.onCancel) option.onCancel();
        dialogRef.close(true);
      } else {
        if (option.onConfirm) option.onConfirm();
      }
    });
  }

  confirmDeleteDialog(option: DialogOptions): void {
    option.title =
      (this.translateService.instant(option.title || 'Success') as string) ||
      'Success';
    option.message =
      (this.translateService.instant(
        option.message || 'Operation has successfully completed'
      ) as string) || 'Operation has successfully completed';
    option.dialogVariant = DialogVariant.YesNo;
    const dialogData = new ConfirmDialogModel(
      option.title,
      option.message,
      option.dialogVariant,
      DialogType.Warning,
      option.confirmColor,
      option.cancelColor,
      option.yesText,
      option.noText
    );

    const dialogRef = option.dialog.open(ConfirmDialogComponent, {
      data: dialogData,
      disableClose: Boolean(option.closeDialog),
    });

    dialogRef.afterClosed().subscribe((isSuccess) => {
      if (!isSuccess) {
        if (option.onCancel) option.onCancel();
        dialogRef.close(true);
      } else {
        if (option.onConfirm) option.onConfirm();
      }
    });
  }

  confirmDialogOk(option: DialogOptions): void {
    option.title =
      (this.translateService.instant(option.title || 'Success') as string) ||
      'Success';
    option.message =
      (this.translateService.instant(
        option.message || 'Operation has successfully completed'
      ) as string) || 'Operation has successfully completed';
    option.dialogVariant = DialogVariant.OK;

    const dialogData = new ConfirmDialogModel(
      option.title,
      option.message,
      option.dialogVariant,
      DialogType.Success
    );

    const dialogRef = option.dialog.open(ConfirmDialogComponent, {
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((isSuccess) => {
      if (!isSuccess) {
        if (option.onCancel) option.onCancel();
        dialogRef.close(true);
      } else {
        if (option.onConfirm) option.onConfirm();
      }
    });
  }
}
